import { useState } from 'react';
import { Link } from 'react-router-dom';
import { endpointsForPage } from '../api-endpoints';
import { AnchorHeading } from '../components/AnchorHeading';
import { OnThisPage } from '../components/OnThisPage';
import { useDocsPage } from '../hooks/useDocsPage';

const NODE_FULL = `const crypto = require('crypto');
const https  = require('https');

const API_KEY     = 'rvly_sk_live_<your-token>';
const SIGN_SECRET = '<your-signing-secret>';
const HOST        = 'api.revvly.ai';

// Omit "amount" to settle the invoice in full.
const payload = JSON.stringify({
  invoiceNumber: 'ACME-00042',
  amount:        400,
  currency:      'USD',
  paidAt:        new Date().toISOString(),
  reference:     'pi_3PqR8s2eZvKYlo2C',
});

const sig = crypto
  .createHmac('sha256', SIGN_SECRET)
  .update(payload) // raw bytes — do not re-serialize
  .digest('hex');

const req = https.request({
  host:   HOST,
  path:   '/api/v1/webhooks/invoice-payment',
  method: 'POST',
  headers: {
    'Content-Type':       'application/json',
    'Content-Length':     Buffer.byteLength(payload),
    'Authorization':      \`Bearer \${API_KEY}\`,
    'X-Signature-SHA256': \`sha256=\${sig}\`,
    // Your own event id — makes the call safe to retry.
    'Idempotency-Key':    'evt_3PqR8s2eZvKYlo2C',
  },
}, (res) => {
  let data = '';
  res.on('data', (chunk) => { data += chunk; });
  res.on('end', () => console.log(JSON.parse(data)));
});

req.write(payload);
req.end();`;

const PYTHON_FULL = `import hashlib, hmac, json, urllib.request

API_KEY     = 'rvly_sk_live_<your-token>'
SIGN_SECRET = '<your-signing-secret>'
URL         = 'https://api.revvly.ai/api/v1/webhooks/invoice-payment'

# Omit 'amount' to settle the invoice in full.
payload = json.dumps({
    'invoiceNumber': 'ACME-00042',
    'amount':        400,
    'currency':      'USD',
    'paidAt':        '2026-07-15T10:30:00Z',
    'reference':     'pi_3PqR8s2eZvKYlo2C',
}, separators=(',', ':')).encode()

sig = hmac.new(SIGN_SECRET.encode(), payload, hashlib.sha256).hexdigest()

req = urllib.request.Request(URL, data=payload, headers={
    'Content-Type':       'application/json',
    'Authorization':      f'Bearer {API_KEY}',
    'X-Signature-SHA256': f'sha256={sig}',
    'Idempotency-Key':    'evt_3PqR8s2eZvKYlo2C',
})
with urllib.request.urlopen(req) as res:
    print(json.load(res))`;

const CURL_FULL = `# Store these in environment variables — never commit secrets.
export REVVLY_TOKEN="rvly_sk_live_<your-token>"
export REVVLY_SECRET="<your-signing-secret>"

# Omit "amount" to settle the invoice in full.
BODY='{"invoiceNumber":"ACME-00042","amount":400,"currency":"USD","reference":"pi_3PqR8s2eZvKYlo2C"}'

SIG=$(echo -n "$BODY" | openssl dgst -sha256 -hmac "$REVVLY_SECRET" | awk '{print $2}')

curl -s -X POST https://api.revvly.ai/api/v1/webhooks/invoice-payment \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer $REVVLY_TOKEN" \\
  -H "X-Signature-SHA256: sha256=$SIG" \\
  -H "Idempotency-Key: evt_3PqR8s2eZvKYlo2C" \\
  -d "$BODY"`;

const RESPONSE_PARTIAL = `HTTP/1.1 200 OK

{
  "invoiceId":         "663b1f2e4a3c2d1e0f8b7a9c",
  "invoiceNumber":     "ACME-00042",
  "paymentStatus":     "PENDING",
  "currency":          "USD",
  "amount":            1000,
  "amountPaid":        400,
  "amountDue":         600,
  "fullyPaid":         false,
  "appliedAmount":     400,
  "replayed":          false,
  "actualPaymentDate": null
}`;

const RESPONSE_FULL = `HTTP/1.1 200 OK

{
  "invoiceId":         "663b1f2e4a3c2d1e0f8b7a9c",
  "invoiceNumber":     "ACME-00042",
  "paymentStatus":     "PAID",
  "currency":          "USD",
  "amount":            1000,
  "amountPaid":        1000,
  "amountDue":         0,
  "fullyPaid":         true,
  "appliedAmount":     600,
  "replayed":          false,
  "actualPaymentDate": "2026-07-15T10:30:00.000Z"
}`;

type Lang = 'node' | 'python' | 'curl';

export function InvoicePaymentsApi() {
  const [lang, setLang] = useState<Lang>('node');
  useDocsPage('Invoice Payment Webhook — Revvly Docs');

  const codeByLang: Record<Lang, string> = {
    node: NODE_FULL,
    python: PYTHON_FULL,
    curl: CURL_FULL,
  };

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/api">API</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Invoice payments</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">API Reference</span>
        <h1>
          Mark an invoice <em>paid</em> from your payment system.
        </h1>
        <p className="dp-oneliner">
          Post a payment to Revvly the moment your processor, bank feed, or
          accounting sync sees the money land &mdash; in full, or a part payment
          that leaves the rest outstanding. No one has to open the dashboard to
          keep receivables current.
        </p>
      </header>

      <OnThisPage endpoints={endpointsForPage('/docs/api/invoice-payments')} />

      {/* ── Authentication ─────────────────────────────────────── */}
      <section className="dp-section">
        <h2>Authentication</h2>
        <p className="dp-section-sub">
          The same two credentials as the{' '}
          <Link to="/docs/api/events">Event Ingestion API</Link>, issued together when you
          create or rotate an API key. Both are shown once and never again.
        </p>

        <div className="dp-fields-table api-field-table-mb-sm">
          <div className="dp-field-row">
            <div className="dp-field-k">Bearer token</div>
            <div className="dp-field-v">
              Sent in the <span className="api-inline">Authorization</span> header.
              Identifies your workspace &mdash; the payment is applied to that
              workspace and no other. The key must hold{' '}
              <span className="api-inline">PAYMENT_WRITE</span> (&ldquo;Can record
              payments&rdquo; when you issue it).
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Signing secret</div>
            <div className="dp-field-v">
              Used to compute the <span className="api-inline">X-Signature-SHA256</span>{' '}
              HMAC over the raw request body. A leaked Bearer token on its own
              cannot mark anything paid.
            </div>
          </div>
        </div>

        <span className="api-code-label">Request headers</span>
        <pre className="api-code">{`Authorization:      Bearer rvly_sk_live_<your-token>
Content-Type:       application/json
X-Signature-SHA256: sha256=<hmac-hex>
Idempotency-Key:    <your-event-id>`}</pre>

        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            Signing works exactly as it does for usage events &mdash; HMAC-SHA256 over
            the <strong>raw body bytes</strong>, keyed with the signing secret, sent as{' '}
            <span className="api-inline">sha256=&lt;hex&gt;</span>. Re-serializing the JSON
            after signing invalidates it. The{' '}
            <Link to="/docs/api/events">Event Ingestion page</Link> covers the mechanics in full.
          </span>
        </div>

        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            Keys issued before signing secrets existed do not have one. Rotate the key
            from <strong>Settings &rarr; API Keys</strong> to provision it. If your key is
            scoped to specific endpoints, include{' '}
            <span className="api-inline">/webhooks/invoice-payment</span> (or{' '}
            <span className="api-inline">/webhooks/*</span>) in that scope.
          </span>
        </div>
      </section>

      {/* ── Endpoint ───────────────────────────────────────────── */}
      <section className="dp-section">
        <AnchorHeading id="post-invoice-payment">Endpoint</AnchorHeading>
        <div className="api-endpoint-bar">
          <span className="api-method api-method-post">POST</span>
          <span className="api-url">https://api.revvly.ai/api/v1/webhooks/invoice-payment</span>
          <span className="api-returns">200 OK</span>
        </div>
        <p className="dp-section-sub">
          One payment per request, against one invoice. Omit{' '}
          <span className="api-inline">amount</span> and Revvly settles whatever is still
          outstanding; send an amount and it is recorded as a part payment.
        </p>
      </section>

      {/* ── Request body ───────────────────────────────────────── */}
      <section className="dp-section">
        <h2>Request body</h2>

        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">invoiceNumber</span>
              <span className="api-badge api-badge-cond">one of</span>
            </div>
            <div className="dp-field-v">
              The number printed on the invoice &mdash; e.g.{' '}
              <span className="api-inline">ACME-00042</span>. Usually the reference your
              processor or bank feed already carries, so you never have to store a
              Revvly id. Supply <strong>either</strong> this <strong>or</strong>{' '}
              <span className="api-inline">invoiceId</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">invoiceId</span>
              <span className="api-badge api-badge-cond">one of</span>
            </div>
            <div className="dp-field-v">
              The internal Revvly invoice id. Use it when you already sync Revvly ids;
              otherwise prefer <span className="api-inline">invoiceNumber</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">amount</span>
              <span className="api-badge api-badge-opt">optional</span>
            </div>
            <div className="dp-field-v">
              Amount collected, in the invoice&rsquo;s currency.{' '}
              <strong>Omit it to pay the invoice in full</strong> &mdash; Revvly applies
              the outstanding balance, whatever it is at that moment. Must be greater
              than zero.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">currency</span>
              <span className="api-badge api-badge-opt">optional</span>
            </div>
            <div className="dp-field-v">
              A safety check, not a conversion: if supplied it must match the
              invoice&rsquo;s currency, otherwise the request is rejected. Revvly does not
              convert a payment made in another currency.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">paidAt</span>
              <span className="api-badge api-badge-opt">optional</span>
            </div>
            <div className="dp-field-v">
              ISO 8601 timestamp for when the money actually moved. Defaults to now.
              Send the real date when you are catching up on a payment received
              earlier &mdash; it is what the invoice is settled on, so your collection
              figures land in the right period.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">reference</span>
              <span className="api-badge api-badge-opt">optional</span>
            </div>
            <div className="dp-field-v">
              Your payment reference &mdash; charge id, bank transaction id. Stored on the
              payment record for reconciliation.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">notes</span>
              <span className="api-badge api-badge-opt">optional</span>
            </div>
            <div className="dp-field-v">
              Free text kept alongside the payment &mdash; &ldquo;wire, part
              payment&rdquo;, &ldquo;settled by credit note&rdquo;.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">tenantId</span>
              <span className="api-badge api-badge-opt">optional</span>
            </div>
            <div className="dp-field-v">
              A guard, not a selector. Your workspace always comes from the API key; if
              you send a <span className="api-inline">tenantId</span> that disagrees with
              the key, the request is refused rather than applied somewhere else. Useful
              as a tripwire when the same integration is wired to more than one
              workspace.
            </div>
          </div>
        </div>

        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            <strong>The Idempotency-Key header is required.</strong> Use your own event
            id &mdash; the processor&rsquo;s event id is ideal. Payment senders retry, and
            this is what stops a retry being counted as a second payment.
          </span>
        </div>
      </section>

      {/* ── Full examples ──────────────────────────────────────── */}
      <section className="dp-section">
        <h2>Full examples</h2>
        <p className="dp-section-sub">
          A complete request &mdash; authentication, signature, idempotency key, and a
          part payment of 400 against a 1&thinsp;000 invoice.
        </p>

        <div className="api-tabs">
          {(['node', 'python', 'curl'] as Lang[]).map((l) => (
            <button
              key={l}
              className={`api-tab ${lang === l ? 'api-tab-active' : ''}`}
              onClick={() => setLang(l)}
            >
              {l === 'node' ? 'Node.js' : l === 'python' ? 'Python' : 'cURL'}
            </button>
          ))}
        </div>
        <pre className="api-code api-code-tabbed">{codeByLang[lang]}</pre>
      </section>

      {/* ── Response ───────────────────────────────────────────── */}
      <section className="dp-section">
        <h2>Response</h2>
        <p className="dp-section-sub">
          The invoice as it stands after the payment, so you can reconcile without a
          follow-up read. <span className="api-inline">amountDue</span> is what is still
          owed; <span className="api-inline">appliedAmount</span> is what this particular
          call moved.
        </p>

        <div className="api-code-wrap">
          <span className="api-code-label">Part payment &mdash; invoice stays open</span>
          <pre className="api-code">{RESPONSE_PARTIAL}</pre>
        </div>

        <div className="api-code-wrap">
          <span className="api-code-label">
            The payment that clears the balance &mdash; invoice settles
          </span>
          <pre className="api-code">{RESPONSE_FULL}</pre>
        </div>

        <h3 className="api-sub-heading">Status codes</h3>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">200</div>
            <div className="dp-field-v">
              Payment applied &mdash; or the call repeated an{' '}
              <span className="api-inline">Idempotency-Key</span> already seen, in which
              case <span className="api-inline">replayed</span> is{' '}
              <span className="api-inline">true</span> and{' '}
              <span className="api-inline">appliedAmount</span> is{' '}
              <span className="api-inline">0</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">400</div>
            <div className="dp-field-v">
              Malformed request &mdash; no invoice reference, an amount that is not a
              positive number, an unparseable <span className="api-inline">paidAt</span>,
              a currency that is not the invoice&rsquo;s, or a missing{' '}
              <span className="api-inline">Idempotency-Key</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">401</div>
            <div className="dp-field-v">
              Bad or missing Bearer token, a signature that does not verify, or a key
              with no signing secret.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">403</div>
            <div className="dp-field-v">
              The key lacks <span className="api-inline">PAYMENT_WRITE</span>, the
              endpoint is outside the key&rsquo;s scope, or the{' '}
              <span className="api-inline">tenantId</span> in the body does not match the
              key.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">404</div>
            <div className="dp-field-v">
              No such invoice in your workspace. Invoices are never visible across
              workspaces, so a wrong key reads as a missing invoice.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">409</div>
            <div className="dp-field-v">
              The invoice is already paid or cancelled, or has nothing left outstanding.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">429</div>
            <div className="dp-field-v">
              Per-key rate limit reached. Retry after the number of seconds in the{' '}
              <span className="api-inline">Retry-After</span> header.
            </div>
          </div>
        </div>
      </section>

      {/* ── Part payments ──────────────────────────────────────── */}
      <section className="dp-section">
        <h2>Part payments</h2>
        <p className="dp-section-sub">
          Each payment is added to the invoice and{' '}
          <span className="api-inline">amountPaid</span> accumulates. Until it covers the
          total, the invoice stays <strong>Pending</strong> or <strong>Overdue</strong>{' '}
          &mdash; it keeps appearing in receivables and keeps drawing payment reminders,
          which is what you want while money is still owed. The payment that clears the
          balance settles the invoice to <strong>Paid</strong>, exactly as marking it paid
          in the dashboard does.
        </p>

        <div className="dp-fields-table api-field-table-mb-sm">
          <div className="dp-field-row">
            <div className="dp-field-k">Instalments</div>
            <div className="dp-field-v">
              Send one call per instalment. Each carries its own{' '}
              <span className="api-inline">Idempotency-Key</span>,{' '}
              <span className="api-inline">paidAt</span>, and{' '}
              <span className="api-inline">reference</span>, and all of them stay on the
              invoice as a record of how it was collected.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Settling the rest</div>
            <div className="dp-field-v">
              Omit <span className="api-inline">amount</span> on the last call and Revvly
              applies whatever is left &mdash; you do not have to work out the remainder.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Overpayment</div>
            <div className="dp-field-v">
              An amount larger than the balance is recorded in full and the invoice
              settles. <span className="api-inline">amountDue</span> does not go negative.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Credit notes</div>
            <div className="dp-field-v">
              An invoice with a negative total has nothing to collect, so it is refused
              with a <span className="api-inline">409</span> rather than taking a payment.
            </div>
          </div>
        </div>

        <div className="api-note">
          <span className="api-note-icon">&#9888;</span>
          <span>
            Receivables analytics &mdash; AR aging, the cash-flow chart, past-due totals
            &mdash; currently count an open invoice at its <strong>full</strong> amount
            rather than its outstanding balance, so a part-paid invoice reads high on
            those dashboards. The invoice itself, and this endpoint, always report the
            true <span className="api-inline">amountPaid</span> and{' '}
            <span className="api-inline">amountDue</span>.
          </span>
        </div>
      </section>

      {/* ── Retries and idempotency ────────────────────────────── */}
      <section className="dp-section">
        <h2>Retries and idempotency</h2>
        <p className="dp-section-sub">
          Safe to retry. Every payment is recorded under the{' '}
          <span className="api-inline">Idempotency-Key</span> you sent, and a key already
          on the invoice is never applied a second time &mdash; including two copies of
          the same delivery arriving at once. A replay returns{' '}
          <span className="api-inline">200</span> with the invoice&rsquo;s current state,
          not an error, so your sender sees success and stops retrying.
        </p>

        <div className="api-status-row">
          <div className="api-status-item">
            <span className="api-status-pill api-status-pill-ok">applied</span>
            <span className="api-status-desc">
              <span className="api-inline">replayed: false</span> &mdash;{' '}
              <span className="api-inline">appliedAmount</span> was added to the invoice.
            </span>
          </div>
          <div className="api-status-item">
            <span className="api-status-pill api-status-pill-dup">replayed</span>
            <span className="api-status-desc">
              <span className="api-inline">replayed: true</span> &mdash; this key was
              already recorded. Nothing was applied; the totals shown are the invoice as
              it already stood.
            </span>
          </div>
          <div className="api-status-item">
            <span className="api-status-pill api-status-pill-err">refused</span>
            <span className="api-status-desc">
              A <span className="api-inline">409</span> means the invoice cannot take a
              payment &mdash; already paid, cancelled, or nothing outstanding. Retrying
              will not change that.
            </span>
          </div>
        </div>

        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            Use a key that is stable across the retries of one payment and different
            between two genuine payments. Your processor&rsquo;s event id does both; a
            timestamp or a random value per attempt does neither.
          </span>
        </div>
      </section>
    </div>
  );
}
